import { Component, OnDestroy, OnInit } from '@angular/core';
import { map } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { HomeVisitRequestService } from '../../core/services/home-visit-request.service';
import { HomeVisitRequestDto } from '@dto';

interface HomeVisitRequestIntakeInfoDetail {
  question: string;
  answer: string;
}

interface HomeVisitRequestIntakeInfo {
  requestedDateTime: string;
  patientId: number;
  patientName: string;
  patientDateOfBirth: string;
  visitReason?: string;
  textifiedIntake?: HomeVisitRequestIntakeInfoDetail[];
}

@Component({
  selector: 'app-home-visit-request-intake-layout',
  templateUrl: './home-visit-request-intake-layout.component.html',
  styleUrls: ['./home-visit-request-intake-layout.component.scss'],
  standalone: false,
})
export class HomeVisitRequestIntakeLayoutComponent implements OnInit, OnDestroy {
  public homeVisitRequestIntakeInfo: HomeVisitRequestIntakeInfo[];
  private homeVisitRequest$: Observable<HomeVisitRequestDto>;
  private destroyed: Subject<boolean> = new Subject();

  constructor(private route: ActivatedRoute, private homeVisitRequestService: HomeVisitRequestService) {}

  ngOnInit() {
    const homeVisitRequestId = this.route.snapshot.params.homeVisitRequestId;

    if (homeVisitRequestId) {
      this.homeVisitRequest$ = this.homeVisitRequestService.getHomeVisitRequest(homeVisitRequestId);
    } else {
      this.homeVisitRequest$ = of(null);
    }

    this.homeVisitRequest$
      .pipe(
        map((homeVisitRequest: HomeVisitRequestDto) => {
          this.homeVisitRequestIntakeInfo = [];

          for (let i = 0; i < homeVisitRequest.patients.length; i++) {
            const homeVisitPatientRequestInfo: HomeVisitRequestIntakeInfo = {
              requestedDateTime: this.getRequestedDateTime(homeVisitRequest.requestedDateTime),
              patientId: homeVisitRequest.patients[i].id,
              patientName: homeVisitRequest.patients[i].firstName + ' ' + homeVisitRequest.patients[i].lastName,
              patientDateOfBirth: homeVisitRequest.patients[i].dateOfBirth,
              visitReason: undefined,
              textifiedIntake: undefined,
            };

            if (homeVisitRequest.visitReasons) {
              for (let j = 0; j < homeVisitRequest.visitReasons.length; j++) {
                if (homeVisitRequest.patients[i].id === homeVisitRequest.visitReasons[j].patientId) {
                  homeVisitPatientRequestInfo.visitReason = homeVisitRequest.visitReasons[j].visitReason;
                  homeVisitPatientRequestInfo.textifiedIntake = this.getTextifiedIntakeDetails(
                    homeVisitRequest.visitReasons[j].textifiedIntake
                  );
                  break;
                }
              }
            }

            this.homeVisitRequestIntakeInfo.push(homeVisitPatientRequestInfo);
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroyed.next(true);
  }

  private getTextifiedIntakeDetails(textifiedIntake: string): HomeVisitRequestIntakeInfoDetail[] {
    const details: HomeVisitRequestIntakeInfoDetail[] = [];

    if (textifiedIntake) {
      const items = textifiedIntake.split('\n');
      for (let i = 0; i < items.length - 1; i += 2) {
        details.push({
          question: items[i],
          answer: items[i + 1],
        });
      }
    }

    return details;
  }

  private getRequestedDateTime(requestedDateTimeUTC: string): string {
    const dateTime = new Date(requestedDateTimeUTC);

    const formattedDate = dateTime.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });

    const formattedTime = dateTime.toLocaleTimeString('en-US', {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    });

    return `${formattedDate} at ${formattedTime}`;
  }
}
